import { lazy, Suspense, useState, useRef } from "react";
import { Button, V2Loader } from "web-design-system";
import { ArrowLeftIcon, PenLineIcon, Loader2 } from "lucide-react";
import { useGenerateSignRequest } from "creso-core/hooks";
import { toast } from "sonner";
import { getCORSSafeURL, getDigioESignUrl, isProduction } from "web-core/utils";
import { StepProps } from "../../../types";

const PdfViewer = lazy(() =>
  import("../pdf-viewer").then((m) => ({ default: m.PdfViewer }))
);

export function ReviewSignStep({
  formData,
  onNext,
  onBack,
  signingEnabled,
}: StepProps & { signingEnabled: boolean }) {
  const { mutate: generateSignRequest, isPending: isCreatingSigningRequest } =
    useGenerateSignRequest(false);
  const { mutateAsync: sendSigningEmail, isPending: isSendingEmail } =
    useGenerateSignRequest(true);
  const [digioUrl, setDigioUrl] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const isPending = isCreatingSigningRequest || isSendingEmail;

  const handleIframeLoad = () => {
    try {
      // Accessing contentWindow.location.href throws a SecurityError while the
      // iframe is on Digio's cross-origin domain. Once it succeeds, the iframe
      // has redirected back to our origin.
      const href = iframeRef.current?.contentWindow?.location.href;
      if (!href) return;
      const status = new URL(href).searchParams.get("status");
      if (status === "success") {
        onNext({ agreedToTerms: true, formSigned: true });
      } else {
        setDigioUrl(null);
      }
    } catch {
      // Still on Digio's domain — ignore.
    }
  };

  const handleSendSigningEmail = () => {
    if (!formData.formId) return;
    sendSigningEmail(formData.formId, {
      onSuccess: () => {
        onNext({ agreedToTerms: true });
      },
      onError: () => {
        toast.error("Failed to send signing email");
      },
    });
  };

  const handleSign = () => {
    if (!formData.formId) return;
    generateSignRequest(formData.formId, {
      onSuccess: (response) => {
        if (!response.access_token?.id) return;
        const env = isProduction() ? "production" : "sandbox";
        const link = getDigioESignUrl(
          env,
          response.document_id,
          response.access_token.id,
          formData.selectedClient?.email ?? ""
        );
        setDigioUrl(link);
      },
      onError: () => {
        toast.error("Failed to generate signing request");
      },
    });
  };

  const handleBack = () => {
    setDigioUrl(null);
    onBack();
  };

  return (
    <div className="flex flex-col gap-8 h-full">
      <div className="flex items-center gap-3">
        <Button
          type="button"
          className="-ml-2"
          variant="ghost"
          size="icon"
          onClick={handleBack}
          disabled={isPending}
        >
          <ArrowLeftIcon className="size-4" />
        </Button>
        <span className="text-2xl font-bold text-foreground">
          Review&nbsp;
          {signingEnabled ? (
            <>
              &amp; <span className="text-gift-foreground">Sign</span>
            </>
          ) : null}
        </span>
      </div>

      <div className="flex-1 min-h-0 rounded-lg overflow-hidden">
        {digioUrl ? (
          <iframe
            ref={iframeRef}
            src={digioUrl}
            className="w-full h-full border rounded-lg border-border"
            allow="camera; microphone"
            onLoad={handleIframeLoad}
          />
        ) : formData.responseForm ? (
          <Suspense fallback={<V2Loader className="size-full" isLoading />}>
            <PdfViewer url={getCORSSafeURL(formData.responseForm)} />
          </Suspense>
        ) : (
          <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
            Form not available
          </div>
        )}
      </div>

      {signingEnabled ? (
        !digioUrl ? (
          <div className="flex items-center justify-between mt-auto">
            {/* HDFC forms are signed by the client via the emailed link only */}
            {formData.amc !== "HDFC" && (
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={handleSign}
                disabled={isPending || !formData.formId}
              >
                {isCreatingSigningRequest ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <PenLineIcon className="size-4" />
                )}
                {isCreatingSigningRequest ? "Preparing…" : "Sign"}
              </Button>
            )}
            <Button
              type="button"
              size="lg"
              className="ml-auto"
              onClick={handleSendSigningEmail}
              disabled={isPending || !formData.formId}
            >
              {isSendingEmail ? (
                <Loader2 className="size-4 animate-spin" />
              ) : null}
              Send Email
            </Button>
          </div>
        ) : null
      ) : (
        <div className="flex items-center justify-between mt-auto">
          <Button
            type="button"
            size="lg"
            onClick={() => onNext({ agreedToTerms: true })}
            disabled={isPending || !formData.formId}
          >
            Continue
          </Button>
        </div>
      )}
    </div>
  );
}
